import { motion } from "framer-motion";
import { DollarSign, Layers, ShieldCheck, Target, Activity } from "lucide-react";
import { Container, Section, Eyebrow, Reveal } from "@/components/kit";

const framework = [
  {
    icon: DollarSign,
    label: "Revenue",
    value: 95,
    desc: "Company revenue band and funding stage signal budget and deal size.",
  },
  {
    icon: Layers,
    label: "Tech Stack",
    value: 88,
    desc: "The tools they run reveal fit, integration surface, and displacement angles.",
  },
  {
    icon: ShieldCheck,
    label: "Authority",
    value: 91,
    desc: "We target economic buyers and champions with real decision power.",
  },
  {
    icon: Target,
    label: "Need",
    value: 84,
    desc: "Observable pain and initiatives that your solution directly resolves.",
  },
  {
    icon: Activity,
    label: "Intent",
    value: 79,
    desc: "Live buying signals — hiring, funding, tech changes, content engagement.",
  },
];

export const IcpFramework = () => (
  <Section data-testid="icp-framework">
    <Container className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
      <div>
        <Eyebrow>The Qualification Framework</Eyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
          Five signals define
          <br />
          a <span className="text-gold">perfect-fit</span> meeting
        </h2>
        <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
          Every account is scored against a five-point model before a single message goes out. Only
          the highest-fit prospects make it into your pipeline.
        </p>
        <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-gold/25 bg-gold/5 px-5 py-4">
          <span className="font-mono-jb text-3xl font-semibold text-gold">87.4</span>
          <span className="text-sm text-white/60 leading-tight">
            Minimum composite
            <br />
            ICP score to qualify
          </span>
        </div>
      </div>

      <div className="space-y-7">
        {framework.map((f, i) => (
          <Reveal key={f.label} delay={i * 0.06}>
            <div className="rounded-2xl border border-white/10 bg-navy-800 p-5 md:p-6 transition-colors duration-300 hover:border-gold/40">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold/10 border border-gold/25 text-gold">
                    <f.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                  </span>
                  <span className="font-display text-lg font-semibold text-white">{f.label}</span>
                </div>
                <span className="font-mono-jb text-sm text-gold">{f.value}%</span>
              </div>
              <p className="mt-3 text-sm text-white/50">{f.desc}</p>
              <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/8">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-gold/70 to-gold"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${f.value}%` }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1.1, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);
